export interface WorkerPool {
    /** Starts workers up to the limit, so jobs queued while others run are picked up in parallel. */
    kick: () => void;
    /** Resolves when no worker is running; rejects with the first error a job threw. */
    idle: () => Promise<void>;
    readonly active: number;
}

/**
 * Runs queued jobs with at most `limit` in flight. `takeNext` must claim the job it
 * returns (e.g. mark it as started) so two workers never pick the same one. A worker
 * ends when `takeNext` returns nothing; call `kick` again after queueing more jobs.
 */
export const createWorkerPool = <T>(
    takeNext: () => T | undefined,
    run: (job: T) => Promise<void>,
    limit: number
): WorkerPool => {
    const max = Math.max(1, limit);
    let active = 0;
    let firstError: { error: unknown } | null = null;
    let waiters: Array<() => void> = [];

    const worker = async () => {
        try {
            for (let job = takeNext(); job !== undefined; job = takeNext()) {
                await run(job);
            }
        } catch (error) {
            firstError = firstError ?? { error };
        } finally {
            active -= 1;
            if (active === 0) {
                waiters.forEach(resolve => resolve());
                waiters = [];
            }
        }
    };

    return {
        kick: () => {
            // A fixed count: a worker that finds the queue empty ends right away.
            for (let missing = max - active; missing > 0; missing -= 1) {
                active += 1;
                void worker();
            }
        },
        idle: () =>
            (active === 0 ? Promise.resolve() : new Promise<void>(resolve => waiters.push(resolve))).then(() => {
                if (firstError) {
                    const { error } = firstError;
                    firstError = null;
                    throw error;
                }
            }),
        get active() {
            return active;
        },
    };
};

/** Runs every job, including ones queued meanwhile, with at most `limit` in flight. */
export const runWorkerPool = async <T>(
    takeNext: () => T | undefined,
    run: (job: T) => Promise<void>,
    limit: number
): Promise<void> => {
    const pool = createWorkerPool(takeNext, run, limit);
    pool.kick();
    await pool.idle();
};
